import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderTextElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.HEADING:
  return isEditingField('content') ? (
    <TextField
      value={tempValue}
      onChange={(e) => setTempValue(e.target.value)}
      onKeyDown={handleKeyPress}
      onBlur={saveInlineEdit}
      autoFocus
      variant="outlined"
      size="small"
      fullWidth
      sx={{ mb: 0 }}
    />
  ) : (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0
      }}
    >
      <ElementControls />
      <Typography
        variant="h6"
        sx={{
          ...element.styles,
          cursor: 'pointer',
          '&:hover': {
            outline: '2px dashed #1976d2',
            outlineOffset: '2px'
          },
          padding: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
            ? element.styles?.padding || '8px 12px'
            : element.styles?.padding || 0, // Match preview padding exactly
          borderRadius: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
            ? '4px'
            : 0,
          display: 'block',
          border: (() => {
            const borderValue = element.styles?.border || 'none';
            console.log('🎨 Heading element border value:', borderValue, 'for element:', element.id);
            return borderValue;
          })(),
          ...getSelectionStyles(element.id)
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          console.log('🔴 Heading clicked - selecting element:', element.id);
          console.log('🔴 Event target:', e.target);
          console.log('🔴 Event currentTarget:', e.currentTarget);
          console.log('🔴 Before selection - selectedElement:', selectedElement?.id);
          console.log('🔴 Before selection - rightPanelOpen:', rightPanelOpen);
          setSelectedElement(element);
          setSelectedElementContext({ isInZone, layoutId, zoneId });
          setTabValue(1); // Set to Style tab
          setRightPanelOpen(true); // Ensure right panel is open
          console.log('🔴 After selection calls made');
        }}
        onDoubleClick={() => handleDoubleClick('content', element.content)}
        title="Click to select and edit styles • Double-click to edit text"
      >
        {element.content || 'Double-click to edit heading'}
      </Typography>
    </Box>
  );

case ELEMENT_TYPES.TEXT:
  return isEditingField('content') ? (
    <Box sx={{ mb: 0.5 }}>
      {/* Rich Text Editor Toolbar */}
      <Box sx={{
        display: 'flex',
        gap: 1,
        mb: 1,
        p: 1,
        backgroundColor: '#f5f5f5',
        borderRadius: 1,
        flexWrap: 'wrap'
      }}>
        {[
          { color: '#ffd6e0', label: 'Pink' },
          { color: '#ffff59', label: 'Yellow' },
          { color: '#aeefff', label: 'Blue' }
        ].map(({ color, label }) => (
          <Button
            key={color}
            size="small"
            variant="outlined"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();

              // Use setTimeout to ensure the click event is processed first
              setTimeout(() => {
                applyHighlight(color, textEditorRef);
              }, 10);
            }}
            sx={{ minWidth: 'auto', px: 1, backgroundColor: color, borderColor: '#e0e0e0', marginRight: 0.5 }}
            title={`Highlight ${label} - Select text first`}
          >
            <span style={{ background: color, padding: '0 4px', fontWeight: 'bold' }}>H</span>
          </Button>
        ))}
        <Button
          size="small"
          variant="outlined"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();

            console.log('Remove highlights button clicked');

            // Simple approach: Find the active contentEditable element
            const activeElement = document.activeElement;
            const contentEditableElements = document.querySelectorAll('[contenteditable="true"]');

            console.log('Active element:', activeElement);
            console.log('ContentEditable elements found:', contentEditableElements.length);

            // Try to find the editor element
            let editor = null;
            if (activeElement && activeElement.contentEditable === 'true') {
              editor = activeElement;
            } else if (contentEditableElements.length > 0) {
              editor = contentEditableElements[contentEditableElements.length - 1]; // Get the last one
            }

            if (!editor) {
              console.log('No editor found');
              return;
            }

            console.log('Using editor:', editor);
            console.log('Editor HTML before:', editor.innerHTML);

            // Simple regex replacement to remove highlight spans
            let newContent = editor.innerHTML;

            // Remove highlight spans with regex
            newContent = newContent.replace(/<span[^>]*style="[^"]*background-color:[^"]*"[^>]*>(.*?)<\/span>/gi, '$1');

            console.log('Editor HTML after regex:', newContent);

            // Update the editor content
            editor.innerHTML = newContent;

            // Update tempValue
            setTempValue(newContent);

            console.log('Highlights removed successfully');
          }}
          sx={{ minWidth: 'auto', px: 1, marginLeft: 1 }}
          title="Remove all highlights"
        >
          🔄
        </Button>

      </Box>

      {/* Rich Text Editor */}
      <Box
        contentEditable
        suppressContentEditableWarning
        ref={(el) => {
          textEditorRef.current = el;
          if (el) {
            // Set initial content when element is first mounted, but don't override if it has highlights
            if (el.innerHTML === '' || (el.innerHTML !== tempValue && !el.innerHTML.includes('<span style="background-color:'))) {
              el.innerHTML = tempValue || '';
            }
            // Focus the editor when it's first created, but don't move cursor
            if (editingElement?.elementId === element.id && editingField === 'content') {
              // Only focus if not already focused to avoid cursor jumping
              if (document.activeElement !== el) {
                el.focus();
                // Only place cursor at the end if this is the initial focus and no text exists
                if (!window.textEditorInitialized && (!tempValue || tempValue.trim() === '')) {
                  window.textEditorInitialized = true;
                  const range = document.createRange();
                  const selection = window.getSelection();
                  try {
                    range.selectNodeContents(el);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                  } catch (e) {
                    // If that fails, just focus
                    el.focus();
                  }
                }
              }
            }
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          e.stopPropagation();

          // Get plain text from clipboard
          const clipboardData = e.clipboardData || window.clipboardData;
          const pastedText = clipboardData.getData('text/plain');

          if (!pastedText) return;

          // Get the current selection
          const selection = window.getSelection();
          if (!selection.rangeCount) return;

          const range = selection.getRangeAt(0);

          // Delete any selected content
          range.deleteContents();

          // Create a text node with the pasted text (plain text, no formatting)
          const textNode = document.createTextNode(pastedText);
          range.insertNode(textNode);

          // Move cursor to the end of inserted text
          range.setStartAfter(textNode);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);

          // Update tempValue to reflect the change
          const editor = e.currentTarget;
          if (editor && editor.innerHTML !== undefined) {
            setTempValue(editor.innerHTML);
          }
        }}
        onInput={(e) => {
          // Use a timeout to batch updates and avoid constant re-renders
          const currentElement = e.currentTarget;
          if (currentElement && currentElement.innerHTML !== undefined) {
            clearTimeout(window.textInputTimeout);
            window.textInputTimeout = setTimeout(() => {
              // Double-check the element still exists when timeout fires
              if (currentElement && currentElement.innerHTML !== undefined) {
                // Only update if the content actually changed to prevent unnecessary re-renders
                if (currentElement.innerHTML !== tempValue) {
                  setTempValue(currentElement.innerHTML);
                }
              }
            }, 100); // Increased timeout to reduce re-renders
          }
        }}
        onKeyDown={(e) => {
          // Handle immediate save on Enter + Shift/Ctrl
          if (e.key === 'Enter' && (e.shiftKey || e.ctrlKey)) {
            e.preventDefault();
            if (e.currentTarget && e.currentTarget.innerHTML !== undefined) {
              setTempValue(e.currentTarget.innerHTML);
              saveInlineEdit();
            }
          } else {
            handleKeyPress(e);
          }
        }}
        onBlur={(e) => {
          if (e.currentTarget && e.currentTarget.innerHTML !== undefined) {
            setTempValue(e.currentTarget.innerHTML);
            saveInlineEdit();
          }
        }}
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          p: 2,
          minHeight: '100px',
          outline: 'none',
          '&:focus': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
          },
          '& *': {
            margin: 0,
            padding: 0
          }
        }}
      />
    </Box>
  ) : (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0, // Remove margin-bottom for inline layouts
        display: 'inline-block' // Force inline-block for inline layouts
      }}
    >
      <ElementControls />
      <Box
        sx={{
          ...element.styles,
          cursor: 'pointer',
          '&:hover': {
            outline: '2px dashed #1976d2',
            outlineOffset: '2px'
          },
          padding: element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent'
            ? element.styles?.padding || '8px 12px'
            : element.styles?.padding || 0, // Match preview padding exactly
          borderRadius: (() => {
            const hasBackground = element.styles?.backgroundColor && element.styles?.backgroundColor !== 'transparent';
            const radiusValue = hasBackground
              ? element.styles?.borderRadius || '4px'
              : element.styles?.borderRadius || 0;
            console.log('🔄 Text element border radius debug:', {
              elementId: element.id,
              hasBackground,
              backgroundColor: element.styles?.backgroundColor,
              borderRadius: element.styles?.borderRadius,
              finalRadius: radiusValue
            });
            return radiusValue;
          })(),

          display: 'block !important', // Force block for proper text wrapping
          minHeight: '20px',
          wordWrap: 'break-word',
          width: '100%', // Take full width of the zone
          whiteSpace: 'normal',
          border: (() => {
            const borderValue = element.styles?.border || 'none';
            console.log('🎨 Text element border value:', borderValue, 'for element:', element.id);
            return borderValue;
          })(),
          ...getSelectionStyles(element.id)
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          console.log('🔴 Text clicked - selecting element:', element.id);
          console.log('🔴 Event target:', e.target);
          console.log('🔴 Event currentTarget:', e.currentTarget);
          console.log('🔴 Before selection - selectedElement:', selectedElement?.id);
          console.log('🔴 Before selection - rightPanelOpen:', rightPanelOpen);
          setSelectedElement(element);
          setSelectedElementContext({ isInZone, layoutId, zoneId });
          setTabValue(1); // Set to Style tab
          setRightPanelOpen(true); // Ensure right panel is open
          console.log('🔴 After selection calls made');
        }}
        onDoubleClick={() => handleDoubleClick('content', element.content)}
        title="Click to select and edit styles • Double-click to edit text • Use toolbar for styling"
        dangerouslySetInnerHTML={{ __html: element.content || 'Double-click to edit text' }}
      />
    </Box>
  );
case ELEMENT_TYPES.QUOTATION_BLOCK:
  console.log('🔴 Rendering quotation block:', element.id, element);
  return isEditingField('content') ? (
    <Box sx={{ mb: 1 }}>
      {/* Rich Text Editor Toolbar */}
      <Box sx={{
        display: 'flex',
        gap: 1,
        mb: 1,
        p: 1,
        backgroundColor: '#f5f5f5',
        borderRadius: 1,
        flexWrap: 'wrap'
      }}>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            document.execCommand('bold', false, null);
          }}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <strong>B</strong>
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            document.execCommand('italic', false, null);
          }}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          <em>I</em>
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setPromptDialogTitle('Enter Color');
            setPromptDialogMessage('Enter color (e.g., #ff0000, red, blue):');
            setPromptDialogValue('#ff0000');
            setPromptAction(() => (color) => {
              if (color) {
                document.execCommand('foreColor', false, color);
              }
            });
            setShowPromptDialog(true);
          }}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          🎨
        </Button>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value=""
            onChange={(e) => {
              if (e.target.value) {
                document.execCommand('fontName', false, e.target.value);
              }
            }}
            displayEmpty
            sx={{ fontSize: '0.75rem', height: '32px' }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200
                }
              }
            }}
          >
            <MenuItem value="" disabled>
              <em>Font</em>
            </MenuItem>
            {getPresetFontFamilies().map((fontFamily) => (
              <MenuItem key={fontFamily} value={fontFamily}>
                {fontFamily.replace(/['"]/g, '').replace(', sans-serif', '').replace(', serif', '').replace(', monospace', '')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setPromptDialogTitle('Enter Font Size');
            setPromptDialogMessage('Enter font size (e.g., 12px, 1.2em, 120%):');
            setPromptDialogValue('16px');
            setPromptAction(() => (size) => {
              if (size) {
                document.execCommand('fontSize', false, size);
              }
            });
            setShowPromptDialog(true);
          }}
          sx={{ minWidth: 'auto', px: 1 }}
        >
          📏
        </Button>
        {[
          { color: '#ffd6e0', label: 'Pink' },
          { color: '#ffff59', label: 'Yellow' },
          { color: '#aeefff', label: 'Blue' }
        ].map(({ color, label }) => (
          <Button
            key={color}
            size="small"
            variant="outlined"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();

              // Use setTimeout to ensure the click event is processed first
              setTimeout(() => {
                applyHighlight(color, textEditorRef);
              }, 10);
            }}
            sx={{ minWidth: 'auto', px: 1, backgroundColor: color, borderColor: '#e0e0e0', marginRight: 0.5 }}
            title={`Highlight ${label} - Select text first`}
          >
            <span style={{ background: color, padding: '0 4px', fontWeight: 'bold' }}>H</span>
          </Button>
        ))}
        <Button
          size="small"
          variant="outlined"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();

            console.log('Remove highlights button clicked');

            // Simple approach: Find the active contentEditable element
            const activeElement = document.activeElement;
            const contentEditableElements = document.querySelectorAll('[contenteditable="true"]');

            console.log('Active element:', activeElement);
            console.log('ContentEditable elements found:', contentEditableElements.length);

            // Try to find the editor element
            let editor = null;
            if (activeElement && activeElement.contentEditable === 'true') {
              editor = activeElement;
            } else if (contentEditableElements.length > 0) {
              editor = contentEditableElements[contentEditableElements.length - 1]; // Get the last one
            }

            if (!editor) {
              console.log('No editor found');
              return;
            }

            console.log('Using editor:', editor);
            console.log('Editor HTML before:', editor.innerHTML);

            // Simple regex replacement to remove highlight spans
            let newContent = editor.innerHTML;

            // Remove highlight spans with regex
            newContent = newContent.replace(/<span[^>]*style="[^"]*background-color:[^"]*"[^>]*>(.*?)<\/span>/gi, '$1');

            console.log('Editor HTML after regex:', newContent);

            // Update the editor content
            editor.innerHTML = newContent;

            // Update tempValue
            setTempValue(newContent);

            console.log('Highlights removed successfully');
          }}
          sx={{ minWidth: 'auto', px: 1, marginLeft: 1 }}
          title="Remove all highlights"
        >
          🔄
        </Button>
      </Box>

      {/* Rich Text Editor */}
      <Box
        contentEditable
        suppressContentEditableWarning
        ref={(el) => {
          textEditorRef.current = el;
          if (el) {
            // Set initial content when element is first mounted, but don't override if it has highlights
            if (el.innerHTML === '' || (el.innerHTML !== tempValue && !el.innerHTML.includes('<span style="background-color:'))) {
              el.innerHTML = tempValue || '';
            }
            // Focus the editor when it's first created, but don't move cursor
            if (editingElement?.elementId === element.id && editingField === 'content') {
              // Only focus if not already focused to avoid cursor jumping
              if (document.activeElement !== el) {
                el.focus();
                // Only place cursor at the end if this is the initial focus and no text exists
                if (!window.textEditorInitialized && (!tempValue || tempValue.trim() === '')) {
                  window.textEditorInitialized = true;
                  const range = document.createRange();
                  const selection = window.getSelection();
                  try {
                    range.selectNodeContents(el);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                  } catch (e) {
                    // If that fails, just focus
                    el.focus();
                  }
                }
              }
            }
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          e.stopPropagation();

          // Get plain text from clipboard
          const clipboardData = e.clipboardData || window.clipboardData;
          const pastedText = clipboardData.getData('text/plain');

          if (!pastedText) return;

          // Get the current selection
          const selection = window.getSelection();
          if (!selection.rangeCount) return;

          const range = selection.getRangeAt(0);

          // Delete any selected content
          range.deleteContents();

          // Create a text node with the pasted text (plain text, no formatting)
          const textNode = document.createTextNode(pastedText);
          range.insertNode(textNode);

          // Move cursor to the end of inserted text
          range.setStartAfter(textNode);
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);

          // Update tempValue to reflect the change
          const editor = e.currentTarget;
          if (editor && editor.innerHTML !== undefined) {
            setTempValue(editor.innerHTML);
          }
        }}
        onInput={(e) => {
          // Use a timeout to batch updates and avoid constant re-renders
          const currentElement = e.currentTarget;
          if (currentElement && currentElement.innerHTML !== undefined) {
            clearTimeout(window.textInputTimeout);
            window.textInputTimeout = setTimeout(() => {
              // Double-check the element still exists when timeout fires
              if (currentElement && currentElement.innerHTML !== undefined) {
                // Only update if the content actually changed to prevent unnecessary re-renders
                if (currentElement.innerHTML !== tempValue) {
                  setTempValue(currentElement.innerHTML);
                }
              }
            }, 100); // Increased timeout to reduce re-renders
          }
        }}
        onKeyDown={(e) => {
          // Handle immediate save on Enter + Shift/Ctrl
          if (e.key === 'Enter' && (e.shiftKey || e.ctrlKey)) {
            e.preventDefault();
            if (e.currentTarget && e.currentTarget.innerHTML !== undefined) {
              setTempValue(e.currentTarget.innerHTML);
              saveInlineEdit();
            }
          } else {
            handleKeyPress(e);
          }
        }}
        onBlur={(e) => {
          if (e.currentTarget && e.currentTarget.innerHTML !== undefined) {
            setTempValue(e.currentTarget.innerHTML);
            saveInlineEdit();
          }
        }}
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          p: 2,
          minHeight: '100px',
          outline: 'none',
          fontStyle: 'italic',
          fontSize: element.styles?.fontSize || '18px',
          fontWeight: element.styles?.fontWeight || '400',
          lineHeight: element.styles?.lineHeight || '1.5',
          fontFamily: element.styles?.fontFamily || "'Inter', sans-serif",
          '&:focus': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
          },
          '& *': {
            margin: 0,
            padding: 0
          }
        }}
      />
    </Box>
  ) : (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 1, // Remove margin-bottom for inline layouts
        display: 'block' // Force block for proper text wrapping
      }}
    >
      <ElementControls />
      {/* Content container - EXACTLY like TEXT elements */}
      <Box
        sx={{
          cursor: 'pointer',
          backgroundColor: element.styles?.backgroundColor || '#fce7f3',
          color: element.styles?.color || '#101022',
          borderRadius: element.styles?.borderRadius || '16px',
          padding: element.styles?.padding || '24px 32px',
          fontStyle: 'italic',
          minHeight: '120px',
          fontSize: element.styles?.fontSize || '18px',
          fontWeight: element.styles?.fontWeight || '400',
          lineHeight: element.styles?.lineHeight || '1.5',
          fontFamily: element.styles?.fontFamily || "'Inter', sans-serif",
          '&:hover': {
            outline: '2px dashed #1976d2',
            outlineOffset: '2px'
          },
          ...element.styles,
          ...getSelectionStyles(element.id)
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          console.log('🔴 Quotation block clicked - selecting element:', element.id);
          setSelectedElement(element);
          setSelectedElementContext({ isInZone, layoutId, zoneId });
          setTabValue(1); // Set to Style tab
          setRightPanelOpen(true); // Ensure right panel is open
          console.log('🔴 After selection calls made');
        }}
        onDoubleClick={() => handleDoubleClick('content', element.content)}
        title="Click to select and edit styles • Double-click to edit text • Use toolbar for styling"
        dangerouslySetInnerHTML={{ __html: element.content || 'Double-click to edit quotation text' }}
      />
    </Box>
  );
    default:
      return null;
  }
};
